import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getterIsAdminValidLogin,
  getterOwnInfo,
  isOwnInfoPlaceholderProfile,
} from '~/store/user/userGetter';
import { closeLoading, openLoading } from '~/store/loading';
import { routePath } from '~/utils/constants';
import {
  getSafePostAdminLoginPath,
  resolveAdminLoginRedirectParam,
} from '~/utils/helper/adminLoginRedirect';
import { isJwtExpired } from '~/utils/helper/cookie';
import { useCommon } from './useCommon';

type TAdminAuthGuardOptions = {
  /**
   * 重新導向到 admin login 後預期回到的路徑。
   * - 提供時：固定導回該路徑（最新消息後台等指定子站使用）
   * - 未提供：使用當前 pathname；若當前已在 login 頁則導回 routePath.admin
   */
  redirectTarget?: string;
  /**
   * 已登入卻停在 /admin/login 時自動跳到主頁。
   * 僅 AdminLayout 需要（其路由家族包含 login 頁本身）。
   */
  jumpAwayFromLoginWhenAuthed?: boolean;
};

/**
 * 共用的 admin 後台守門邏輯：
 * 1. 無 token → 帶 `redirect` 參數導向 admin login
 * 2. 有 token 但 ownInfo 尚未補齊 → 開 loading
 * 3. ownInfo 補齊但驗證失敗 → 同樣導向 admin login
 * 4. (選用) 已登入但卡在 login → 跳回安全主頁
 */
export const useAdminAuthGuard = ({
  redirectTarget,
  jumpAwayFromLoginWhenAuthed = false,
}: TAdminAuthGuardOptions = {}) => {
  const ownInfo = useSelector(getterOwnInfo);
  const profileReady = !isOwnInfoPlaceholderProfile(ownInfo);
  const isValidLogin = useSelector(getterIsAdminValidLogin);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const locationState: unknown = location.state;
  const { getToken, deleteToken } = useCommon();

  const goAdminLogin = useCallback(
    (target: string) => {
      navigate(
        `${routePath.adminLogin}?redirect=${encodeURIComponent(target)}`,
        {
          replace: true,
          state: { redirect: target },
        },
      );
    },
    [navigate],
  );

  useEffect(() => {
    const token = getToken();
    if (!token || isJwtExpired(token)) {
      if (token) {
        deleteToken();
      }
      // redirectTarget 已指定 → 一定要回到該頁；否則按目前頁面，但若已在 login 就不重跳
      if (redirectTarget) {
        goAdminLogin(redirectTarget);
      } else if (pathname !== routePath.adminLogin) {
        goAdminLogin(pathname);
      }
      return;
    }
    if (!profileReady) {
      openLoading();
      return;
    }
    closeLoading();

    if (!isValidLogin) {
      const target =
        redirectTarget ??
        (pathname === routePath.adminLogin ? routePath.admin : pathname);
      const redirectInUrl =
        pathname === routePath.adminLogin
          ? new URLSearchParams(search).get('redirect')
          : null;
      const alreadyAtLoginWithReturn =
        pathname === routePath.adminLogin && redirectInUrl === target;
      if (!alreadyAtLoginWithReturn) {
        goAdminLogin(target);
      }
      return;
    }

    if (jumpAwayFromLoginWhenAuthed && pathname === routePath.adminLogin) {
      navigate(
        getSafePostAdminLoginPath(
          resolveAdminLoginRedirectParam(search, locationState),
        ),
        { replace: true },
      );
    }
  }, [
    ownInfo,
    profileReady,
    isValidLogin,
    pathname,
    search,
    locationState,
    redirectTarget,
    jumpAwayFromLoginWhenAuthed,
    getToken,
    deleteToken,
    goAdminLogin,
    navigate,
  ]);
};
